import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import { Query, QueryGetVisitorGroupsForTenantArgs, WeekScheduleDay } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS } from 'graphql/v2/queries';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';
import { getUserGroupPath } from 'v2/paths';

export const CREATE_GROUP_STEP_1_FORM_ID = 'create-group-step-1';
const STEP_INDEX = 0;

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;

export interface ScheduleGrant {
  id: string;
  weekDays: WeekScheduleDay[];
}

interface FormValues {
  name: string;
}

interface Props {
  existingGroupNames: string[];
  onGroupCreated: (groupId: string, groupName: string, scheduleGrant: ScheduleGrant | null) => void;
}

const GroupNameStep: React.FC<Props> = ({ existingGroupNames, onGroupCreated }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToNext, setFooterContent, steps } = useWizardStepContext();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({ mode: 'onTouched', defaultValues: { name: '' } });

  const { addVisitorGroupWithMaxNumberOfMembers } = useExternalUsers({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const [fetchGroupsWithSchedules] = useLazyQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    fetchPolicy: 'network-only'
  });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const createGroup = React.useCallback(
    async (name: string) => {
      const result = await addVisitorGroupWithMaxNumberOfMembers(name, null);
      if (!result?.addVisitorGroup) return null;

      const newGroup = result.addVisitorGroup;
      let scheduleData;
      try {
        scheduleData = await fetchGroupsWithSchedules();
      } catch {
        // The group already exists at this point, so a failed schedule lookup must not undo the creation flow.
      }
      const groupData = scheduleData?.data?.getVisitorGroupsForTenant.find((grp) => grp.id === newGroup.id);

      let scheduleGrant: ScheduleGrant | null = null;
      if (groupData) {
        const grant = groupData.accessGrants[0];
        if (grant) scheduleGrant = { id: grant.id, weekDays: grant.accessSchedule.weekDays };
      }

      navigate(getUserGroupPath(newGroup.id));

      return { groupId: newGroup.id, groupName: newGroup.name, scheduleGrant };
    },
    [addVisitorGroupWithMaxNumberOfMembers, fetchGroupsWithSchedules, navigate]
  );

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      const group = await createGroup(data.name);
      if (group) {
        onGroupCreated(group.groupId, group.groupName, group.scheduleGrant);
        goToNext();
      }
    } catch {
      // Error displayed via snackbar in useExternalUsers
    } finally {
      setIsLoading(false);
    }
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="group-name-step-submit"
          id="group-name-submit-button"
          variant="contained"
          type="submit"
          form={CREATE_GROUP_STEP_1_FORM_ID}
          loading={isLoading}
          endIcon={<ChevronRightIcon />}
        >
          {translation.create_and_set_up}
        </Button>
      </Box>
    );
  }, [isLoading, setFooterContent, steps, translation]);

  return (
    <Stack component="form" id={CREATE_GROUP_STEP_1_FORM_ID} onSubmit={onSubmit} noValidate gap={3}>
      <TextField
        {...register('name', {
          required: translation.name_cannot_be_empty,
          validate: (value) => !existingGroupNames.includes(value) || translation.group_name_unique_error
        })}
        id="name-input"
        label={translation.group_name}
        variant="outlined"
        error={!!errors.name}
        helperText={errors.name?.message}
        autoFocus
      />
      <Alert severity="info">{translation.new_group_wizard_info}</Alert>
    </Stack>
  );
};

export default GroupNameStep;
